"use client";

import { initCheckoutSessionAction } from "@/app/actions/subscription/fiat/stripe-actions";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToggle } from "@/hooks/use-toggle";
import { formatSubscriptionPrice } from "@/lib/format-subscription";
import { cn } from "@/lib/utils";
import { useAuthDialogContext } from "@/providers/auth-dialog-provider";
import { PropsWithClassName } from "@/types";
import { isNil } from "@/utils/is-nil";
import { SubscriptionPlanConfig } from "@prisma/client";
import { CheckIcon } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import React, { use } from "react";
import { toast } from "sonner";
import Stripe from "stripe";
import { Modal, ModalContent, ModalHeader, ModalTitle } from "../../ui/modal";
import { Checkout } from "../checkout";

function isEnterprisePlan(count: number) {
  return count > 100_000_000;
}

export function PricingCard(
  props: PropsWithClassName<{
    isActive?: boolean;
    productName: string;
    pricesPromise?: Promise<Stripe.Price[]>;
    productConfig: Omit<
      SubscriptionPlanConfig,
      "id" | "createdAt" | "updatedAt"
    >;
    isPreview?: boolean;
  }>,
) {
  const { data: session } = useSession();
  const authDialogContext = useAuthDialogContext();
  const t = useTranslations("Pricing");
  const checkoutToggle = useToggle();
  const searchParams = useSearchParams()?.get("billing-period") ?? "monthly";

  const prices = use(props.pricesPromise ?? Promise.resolve([]));
  const price = prices.find((price) => {
    if (price.active) {
      if (searchParams === "yearly") {
        return price.recurring?.interval === "year";
      }
      if (searchParams === "monthly") {
        return price.recurring?.interval === "month";
      }
    }
    return false;
  });

  if (!price || isNil(price.unit_amount)) {
    return null;
  }

  const formattedPrice = formatSubscriptionPrice(
    price.unit_amount,
    price.currency,
    price.recurring?.interval,
  );

  const contactUrl = `mailto:${process.env.NEXT_PUBLIC_SUPPORT_EMAIL}?subject=${encodeURIComponent(
    t("enterprise.contactSubject"),
  )}`;

  const choosePlan = async () => {
    if (!session?.user) {
      authDialogContext.onOpenChange(true);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("priceId", price.id);
      const result = await initCheckoutSessionAction(formData);
      checkoutToggle.toggle(result.success);
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error(t("error.plan.title"), {
        description: t("error.plan.description"),
      });
    }
  };

  const isEnterprise = isEnterprisePlan(props.productConfig.portfolioLimit);

  return (
    <>
      <Card
        className={cn(
          "relative flex scale-[0.97] flex-col transition-all duration-300 ease-in-out",
          props.className,
          props.productConfig.isPopular ? "border-2 border-primary" : "",
          props.isActive
            ? "border-2 border-warning"
            : "hover:scale-[0.99] hover:brightness-[0.97] dark:hover:brightness-125",
        )}
      >
        <CardHeader className="pb-4">
          <div className="absolute right-3 top-3 flex gap-2">
            {/* {!isEnterprise && (
              <Badge
                variant="destructive"
                className="select-none hover:bg-destructive"
              >
                -90%
              </Badge>
            )} */}

            {props.productConfig.isPopular ? (
              <Badge
                variant="secondary"
                className="select-none bg-primary text-primary-foreground hover:bg-primary"
              >
                {t("popular")}
              </Badge>
            ) : null}
          </div>

          <CardTitle className="text-2xl font-semibold text-foreground">
            {props.productName}
          </CardTitle>
          {/* {props.product.description ? (
          <CardDescription className="pt-1">
            {props.product.description}
          </CardDescription>
        ) : null} */}
        </CardHeader>
        <CardContent className="flex-grow">
          {isEnterprise ? (
            <div className="mb-6">
              <span className="text-4xl font-extrabold text-foreground">
                {t("enterprise.price")}
              </span>
            </div>
          ) : (
            <div className="mb-6">
              <span className="text-4xl font-extrabold text-foreground">
                {formattedPrice.amount}
              </span>
              {formattedPrice.interval ? (
                <span className="text-muted-foreground">
                  /{t(`interval.${formattedPrice.interval}`)}
                </span>
              ) : null}
            </div>
          )}
          <ul className="space-y-3">
            <PricingFeatureItem
              feature={t("product.portfolios", {
                count: isEnterprisePlan(props.productConfig.portfolioLimit)
                  ? 0
                  : props.productConfig.portfolioLimit.toLocaleString(),
              })}
            />
            <PricingFeatureItem
              feature={t("product.transactions", {
                count: isEnterprisePlan(props.productConfig.transactionLimit)
                  ? 0
                  : props.productConfig.transactionLimit.toLocaleString(),
              })}
            />
            <PricingFeatureItem
              feature={t("product.syncs", {
                count: isEnterprisePlan(props.productConfig.syncsPerDay)
                  ? 0
                  : props.productConfig.syncsPerDay.toLocaleString(),
              })}
            />
            {props.productConfig.macroEconomy ? (
              <PricingFeatureItem feature={t("product.macro")} />
            ) : null}
            {props.productConfig.assetScreener ? (
              <PricingFeatureItem feature={t("product.screener")} />
            ) : null}
            {props.productConfig.apiAccess ? (
              <PricingFeatureItem feature={t("product.apiAccess")} />
            ) : null}
            {props.productConfig.commercialUse ? (
              <PricingFeatureItem feature={t("product.commercialUse")} />
            ) : null}
            <PricingFeatureItem
              feature={t(`support.${props.productConfig.supportLevel}`)}
            />
          </ul>
        </CardContent>
        <CardFooter>
          {!props.isPreview ? (
            <>
              {/* Enterprise plans should contact us. Others can choose a plan */}
              {props.isActive ? (
                <div
                  className={buttonVariants({
                    variant: "secondary",
                    size: "lg",
                    className: "w-full",
                  })}
                  aria-disabled
                >
                  {t("active")}
                </div>
              ) : props.productConfig.portfolioLimit > 1_000_000 ? (
                <Link
                  href={contactUrl}
                  className={buttonVariants({
                    variant: "outline",
                    size: "lg",
                    className: "w-full",
                  })}
                  target="_blank"
                >
                  {t("choose", { name: props.productName })}
                </Link>
              ) : (
                <Button
                  className="w-full"
                  size="lg"
                  variant={
                    props.productConfig.isPopular ? "default" : "outline"
                  }
                  onClick={choosePlan}
                >
                  {t("choose", { name: props.productName })}
                </Button>
              )}
            </>
          ) : null}
        </CardFooter>
      </Card>
      {checkoutToggle.value ? (
        <CheckoutModal
          open={checkoutToggle.value}
          onOpenChange={checkoutToggle.toggle}
        />
      ) : null}
    </>
  );
}

function CheckoutModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("Pricing.checkout");
  return (
    <Modal isRoute={false} open={props.open} onOpenChange={props.onOpenChange}>
      <ModalContent className="max-h-[96%] lg:max-w-3xl">
        <ModalHeader>
          <ModalTitle>{t("title")}</ModalTitle>
        </ModalHeader>
        <div className="min-h-[512px] overflow-hidden rounded-lg">
          <Checkout />
        </div>
      </ModalContent>
    </Modal>
  );
}

function PricingFeatureItem(props: {
  icon?: React.ReactNode;
  feature: string;
}) {
  return (
    <li className="flex items-center">
      {props.icon ?? <CheckIcon className="mr-2 size-5 text-primary" />}
      <span className="text-muted-foreground">{props.feature}</span>
    </li>
  );
}
